import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, ShieldQuestion } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiClientError, api } from "../lib/api";

interface Module {
  key: string;
  labelKey: string;
}
interface Pending {
  email: string;
  name: string | null;
}

/**
 * Stack §7c: someone who authenticates through SSO but holds no GAC grant gets a
 * page to ask for the modules they need, rather than a dead end. Their identity
 * is already proven — this page only collects what they want and why.
 */
export default function RequestAccess() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  const [note, setNote] = useState("");

  const pendingQ = useQuery({
    queryKey: ["gac", "pending"],
    queryFn: async () => {
      try {
        return await api<Pending>("/auth/pending");
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) return null;
        throw err;
      }
    },
    retry: false,
  });

  const modulesQ = useQuery({
    queryKey: ["gac", "modules"],
    queryFn: () => api<Module[]>("/auth/modules"),
  });

  const submit = useMutation({
    mutationFn: () =>
      api<{ id: string; status: string }>("/auth/request-access", {
        method: "POST",
        body: JSON.stringify({ scopes: selected, note: note.trim() || undefined }),
      }),
  });

  const toggle = (key: string, on: boolean) =>
    setSelected((prev) => (on ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));

  if (submit.isSuccess) {
    return (
      <section className="mx-auto max-w-lg py-8">
        <Card>
          <CardHeader className="text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-[hsl(var(--success))]" aria-hidden />
            <CardTitle asChild className="text-2xl">
              <h1>{t("gac:submittedTitle")}</h1>
            </CardTitle>
            <CardDescription>{t("gac:submittedBody")}</CardDescription>
          </CardHeader>
        </Card>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-lg py-8">
      <Card>
        <CardHeader>
          <ShieldQuestion className="h-8 w-8 text-muted-foreground" aria-hidden />
          <CardTitle asChild className="text-2xl">
            <h1>{t("gac:title")}</h1>
          </CardTitle>
          <CardDescription>
            {pendingQ.data
              ? t("gac:introSignedIn", { email: pendingQ.data.email })
              : t("gac:intro")}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          <fieldset className="space-y-3 border-0 p-0">
            <legend className="mb-2 text-sm font-medium">{t("gac:whichAreas")}</legend>
            {(modulesQ.data ?? []).map((m) => (
              <div key={m.key} className="flex items-start gap-3">
                <Checkbox
                  id={`scope-${m.key}`}
                  checked={selected.includes(m.key)}
                  onCheckedChange={(v) => toggle(m.key, v === true)}
                  className="mt-1"
                />
                <div>
                  <Label htmlFor={`scope-${m.key}`}>{t(m.labelKey)}</Label>
                  <p className="text-xs text-muted-foreground">{t(`${m.labelKey}Hint`)}</p>
                </div>
              </div>
            ))}
          </fieldset>

          <div>
            <Label htmlFor="gac-note">{t("gac:whyLabel")}</Label>
            <Input
              id="gac-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("gac:whyPlaceholder")}
              className="mt-1"
            />
          </div>

          {submit.isError && (
            <p className="text-sm text-destructive">
              {submit.error instanceof ApiClientError
                ? submit.error.message
                : t("gac:submitFailed")}
            </p>
          )}

          <Button
            type="button"
            className="w-full"
            disabled={selected.length === 0 || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? t("gac:submitting") : t("gac:submit")}
          </Button>

          <p className="text-center text-xs text-muted-foreground">{t("gac:footnote")}</p>
        </CardContent>
      </Card>
    </section>
  );
}
